/**
 * DateInput Component
 */

import React, {
  FC,
  useState,
  useCallback,
  useRef,
  useMemo,
  createRef,
} from 'react';
import isValid from 'date-fns/isValid';
import format from 'date-fns/format';
import dateFnsParse from 'date-fns/parse';
import COLORS from '../../style/universal.color';
import useDidUpdate from '../hooks/useDidUpdate';
import {
  DateInputProps,
  HandleInputChangeProps,
  HandleInputKeyDownProps,
  FocusEventWithData,
} from './dateInput.types';
import {
  Wrapper,
  InnerInputs,
  Input,
  Divider,
  ResetBtn,
  ErrorMsg,
} from './dateInput.style';
import {
  validateDate,
  useParsedDate,
  formatCallbackObj,
  formatDisplayValue,
  baseDate,
  utcDate,
} from './utils';

const DateInput: FC<DateInputProps> = (props: DateInputProps) => {

  const {
    className,
    value,
    divider = '/',
    inputFormat = 'MM/dd/yyyy',
    outputFormat = 'yyyy-MM-dd',
    handlers = {},
    invalidDateMessage = 'Invalid Date',
    maxDate: __maxDate,
    maxDateMessage,
    minDate: __minDate,
    minDateMessage,
    resetBtnLabel = 'Reset',
    resetBtnHandlers = {},
  } = props;

  const {
    onBlur,
    onChange,
    onClick,
    onFocus,
    onMouseDownCapture,
    onMouseUpCapture,
    ...otherHandlers
  } = handlers;

  const {
    onClick: onResetBtnClick,
    ...otherResetBtnHandlers
  } = resetBtnHandlers;

  const minDate = useParsedDate(__minDate);
  const maxDate = useParsedDate(__maxDate);

  // Array of metadata for building inner inputs generated by parsing the inputFormat
  const innerInputs = useMemo(() => inputFormat.split(divider).map((formatString, index) => ({
    format: formatString,
    placeholder: formatString.toUpperCase(),
    name: formatString.toUpperCase(), // Name used for property in state object
  })), [
    inputFormat,
    divider
  ]);

  // Reference to array of 'n' number of inner input references ( MM is one input, dd is another, etc)
  const inputsRef = useRef(innerInputs.map(() => createRef<HTMLInputElement>()));
  // ResetBtn reference
  const resetBtnRef = useRef<HTMLButtonElement>(null);
  // Is the user clicking (for auto-select on click - see `handleInputFocus`)
  const isUsingMouseRef = useRef<Boolean>(true);
  // Did the user enter a leading zero?
  const isLeadingZeroEnteredRef = useRef<Boolean>(false);
  // Is the user deleting?
  const isDeleteRef = useRef<Boolean>(false);
  // character count for active/current input
  const charCountRef = useRef<number>(0);
  // State management for validation message visibility
  const [isValidationVisible, setIsValidationVisible] = useState(false);

  // Empty dynamic state object.
  const emptyState = useMemo(() => innerInputs.reduce((acc, input) => {
    acc[input.name] = '';
    return acc;
  }, {}), [
    innerInputs,
  ]);

  // Format dynamic state object
  const formatStateObject = useCallback((
    val: string | number | Date | undefined,
  ) => {

    if (!val) {
      return emptyState;
    }

    const date = utcDate(new Date(val));

    return isValid(date) ?
      innerInputs.reduce((acc, input) => {
        acc[input.name] = input.format ? format(date, input.format) : '';
        return acc;
      }, {})
      : emptyState;

  }, [
    innerInputs,
    emptyState,
  ]);

  // Memoize formatted state object
  const getValue = useCallback(() => formatStateObject(value), [
    value,
    formatStateObject,
  ]);

  // Internal State management
  const [innerInputValue, setInnerInputValue] = useState(getValue());

  // Controlled component - updates to state on value changes
  useDidUpdate(() => {

    if (value === null || isValid(value)) {
      setInnerInputValue(getValue());
    }

  }, [
    setInnerInputValue,
    getValue,
    value,
  ]);

  // String representation of the current state
  const enteredString = useMemo(() => Object.values(innerInputValue).join(divider), [
    innerInputValue,
    divider,
  ]);

  // Parsed Date from the current state
  const parsedDate = useMemo(() => dateFnsParse(enteredString, inputFormat, baseDate), [
    enteredString,
    inputFormat
  ]);

  // Get validation error message... will return null if no error
  const validationError = validateDate(parsedDate, {
    invalidDateMessage,
    maxDate,
    maxDateMessage,
    minDate,
    minDateMessage,
  });

  // Determine if the field is empty
  const isInnerInputEmpty = useMemo(() => !Object.values(innerInputValue).some((input) => (input !== null && input !== '')), [
    innerInputValue,
  ]);

  // Flag to determine if we should display validation error
  const isDateRejected = !isInnerInputEmpty && validationError && isValidationVisible;

  // onChange handler is fired when the internal state changes
  useDidUpdate(() => {

    if (onChange) {

      onChange(formatCallbackObj({
        date: parsedDate,
        outputFormat,
        enteredString,
        valid: validationError === null,
      }));

    }

  }, [
    outputFormat,
    enteredString,
    parsedDate,
    validationError,
  ]);

  // Select the input when a user focuses on input if using mouse/touch
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {

    if (isUsingMouseRef.current) {
      e.target.select();
    }

  };

  // Sanitize keys supported by input
  // Support next/prev node focus via specific keys
  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    options?: HandleInputKeyDownProps
  ) => {

    const {
      prevRefNode = null,
      nextRefNode = null,
    } = options || {};

    isDeleteRef.current = false;
    isLeadingZeroEnteredRef.current = false;

    if (e.key === '0' && e.currentTarget.selectionStart === 0) {
      isLeadingZeroEnteredRef.current = true;
    }

    const isDelete = e.key === 'Backspace';

    if (isDelete) {

      isDeleteRef.current = true;
      charCountRef.current = charCountRef.current - 1;

      const val = e.currentTarget.value;

      if (val.length === 0 && prevRefNode) {
        prevRefNode.focus();
      }

    }

    if (e.key === 'Enter' && resetBtnRef.current) {

      resetBtnRef.current.focus();

    }

    if (e.key === 'ArrowLeft' && prevRefNode) {

      prevRefNode.focus();
      prevRefNode.select();

    }

    if (e.key === 'ArrowRight' && nextRefNode) {

      nextRefNode.focus();
      nextRefNode.select();

    }

    // Allowed non-numerical keys
    if (
      isDelete
      || e.key === 'Tab'
      || e.ctrlKey
      || e.metaKey
    ) {

      return;

    }

    // Prevent non-numbers
    if (isNaN(Number(e.key))) {

      e.preventDefault();
      return;

    }

  };

  // Validate input
  // auto-format and shift input focus if appropriate
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    options: HandleInputChangeProps
  ) => {

    const {
      name,
      dateFormat,
      nextRefNode = null,
    } = options;

    const rawValue = e.currentTarget.value;
    const number = Number(rawValue);

    if (isNaN(number) || !name) {

      return;

    }

    const val = number.toString();
    const text = val === '0' && !isLeadingZeroEnteredRef.current ? '' : val;
    const maxLength = dateFormat.length;
    const date = dateFnsParse(text, dateFormat, baseDate);
    const isValidDate = isValid(date);

    charCountRef.current = rawValue.length;

    if (isValidDate || isDeleteRef.current || isLeadingZeroEnteredRef.current) {
      setInnerInputValue({
        ...innerInputValue,
        [name]: text
      });
    }

    const date10x = dateFnsParse(`${text}0`, dateFormat, new Date());

    if (
      text.length
      && (
        !isValidDate && !isLeadingZeroEnteredRef.current
        || isValidDate && text.length <= maxLength && !isValid(date10x)
        || isValidDate && charCountRef.current >= maxLength
      )
    ) {

      if (!nextRefNode) {
        return;
      }

      nextRefNode.focus();
      nextRefNode.select();
      charCountRef.current = 0;

    }

  };

  const handlePaste = (e: React.ClipboardEvent) => {
    // For now only allowing a full date paste...
    // Not sure partial date paste is meaningful enough.
    // (input type="date" does not support paste of any kind)

    // Get pasted data via clipboard API
    const { clipboardData } = e;
    const pastedData = clipboardData.getData('Text');
    const parsedPastedDate = dateFnsParse(pastedData, inputFormat, baseDate);

    if (isValid(parsedPastedDate)) {

      const pasteValue = innerInputs.reduce((acc, input) => {
        acc[input.name] = format(parsedPastedDate, input.format);
        return acc;
      }, {});

      setInnerInputValue(pasteValue);

      if (inputsRef.current.length >= 1) {
        inputsRef.current[inputsRef.current.length - 1].current!.focus();
      }

    }

    // Stop data actually being pasted
    e.stopPropagation();
    e.preventDefault();
    return;

  };

  const handleWrapperClick = (e: React.MouseEvent) => {

    const firstInput = inputsRef.current[0].current;

    if (firstInput) {
      firstInput.focus();
      firstInput.select();
    }

    if (onClick) {
      onClick(e);
    }

  };
  const handleWrapperFocus = (e: React.FocusEvent<HTMLInputElement>) => {

    // Focus on the first input if empty
    if (
      isInnerInputEmpty
      && inputsRef.current[0].current
      && !inputsRef.current[0].current.value
    ) {

      setIsValidationVisible(false);
      inputsRef.current[0].current.focus();
      e.stopPropagation();

    }

    // need to make sure we are not bubbling out internal focus events
    if (
      onFocus
      && (resetBtnRef.current ? e.relatedTarget !== resetBtnRef.current : true)
      && !e.currentTarget.contains(e.relatedTarget as Node)
    ) {
      onFocus(e);
    }

  };
  const handleWrapperMouseDown = (e: React.MouseEvent) => {

    isUsingMouseRef.current = true;

    if (onMouseDownCapture) {
      onMouseDownCapture(e);
    }

  };
  const handleWrapperMouseUp = (e: React.MouseEvent) => {

    isUsingMouseRef.current = false;

    if (onMouseUpCapture) {
      onMouseUpCapture(e);
    }

  };

  const handleInputsContainerBlur = (e: FocusEventWithData) => {

    if (!e.currentTarget.contains(e.relatedTarget as Node)) {

      setIsValidationVisible(true);

      if (onBlur) {

        e.data = formatCallbackObj({
          date: parsedDate,
          outputFormat,
          enteredString,
          valid: validationError === null,
        });
        onBlur(e);

      }

    }

  };

  const handleReset = () => {

    charCountRef.current = 0;
    setInnerInputValue(emptyState);

    if (onResetBtnClick) {
      onResetBtnClick();
    }

  };

  return (
    <>
      <Wrapper
        className={className}
        onClick={handleWrapperClick}
        onFocusCapture={handleWrapperFocus}
        onMouseDownCapture={handleWrapperMouseDown}
        onMouseUpCapture={handleWrapperMouseUp}
        isInvalid={isDateRejected}
        {...otherHandlers}
      >
        <InnerInputs
          onFocus={() => setIsValidationVisible(false)}
          onBlur={handleInputsContainerBlur}
        >
          {innerInputs.map((input, index) => (
            <React.Fragment key={index}>
              {index !== 0 && <Divider>{divider}</Divider>}
              <Input
                ref={inputsRef.current[index]}
                value={formatDisplayValue({
                  value: innerInputValue[input.name],
                  dateFormat: input.format,
                  skip: isLeadingZeroEnteredRef.current && inputsRef.current[index]?.current === document?.activeElement,
                })}
                placeholder={input.placeholder}
                tabIndex={isInnerInputEmpty ? index === 0 ? '0' : '-1' : '0'}
                type={'tel'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, {
                  name: input.name,
                  dateFormat: input.format,
                  nextRefNode: inputsRef.current[index + 1]?.current,
                })}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleInputKeyDown(e, {
                  prevRefNode: inputsRef.current[index - 1]?.current,
                  nextRefNode: inputsRef.current[index + 1]?.current,
                })}
                onFocus={handleInputFocus}
                onPaste={handlePaste}
                onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
              />
            </React.Fragment>
          ))}
        </InnerInputs>
        {!isInnerInputEmpty && (
          <ResetBtn
            type={'reset'}
            aria-label={resetBtnLabel}
            ref={resetBtnRef}
            handlers={{
              onClick: handleReset,
              ...otherResetBtnHandlers
            }}
            shape={'hollow-borderless'}
            iconName={'close'}
            iconSize={13}
            iconColor={COLORS.mlbGrey3}
            hasHover={false}
          />
        )}
      </Wrapper>
      {
        isDateRejected && (
          <ErrorMsg>{validationError}</ErrorMsg>
        )
      }
    </>
  );

};

export default DateInput;
